import { useState, useEffect } from 'react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import OrderService from '../services/order.service';
import PaymentService from '../services/payment.service';
import AddressService from '../services/address.service';
import UserService from '../services/user.service';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { CreditCard, Plus, Check, MapPin } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { InputField } from '../components/ui/InputField';
import { formatPrice } from '../utils/currency';

const CheckoutPage = () => {
    const { cart, fetchCart } = useCart();
    const { user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    // Coupon State
    const { couponCode: initialCouponCode, discount: initialDiscount } = location.state || {};
    const [couponCode] = useState(initialCouponCode || '');
    const [discount] = useState(initialDiscount || 0);

    const [loading, setLoading] = useState(false);
    const [addresses, setAddresses] = useState([]);
    const [selectedAddressId, setSelectedAddressId] = useState(null);
    const [isAddingNew, setIsAddingNew] = useState(false);

    // Payment State
    const [savedCards, setSavedCards] = useState([]);
    const [paymentMethod, setPaymentMethod] = useState('new_card'); // 'new_card' or saved_card_id
    const [saveCard, setSaveCard] = useState(false);

    // New Address Form State
    const [newAddress, setNewAddress] = useState({
        contact_name: user?.first_name ? `${user.first_name} ${user.last_name}` : '',
        contact_phone: user?.phone || '',
        address_type: 'shipping',
        gps_address: '',
        street_address: '',
        area: '',
        apartment: '',
        city: '',
        state: '',
        postal_code: '',
        country: 'Ghana',
        latitude: null,
        longitude: null,
    });

    const [fetchingGps, setFetchingGps] = useState(false);

    const fetchDetailsFromGps = async () => {
        if (!newAddress.gps_address) {
            toast.error('Please enter a GhanaPostGPS address first.');
            return;
        }
        setFetchingGps(true);
        try {
            const formData = new URLSearchParams();
            formData.append('address', newAddress.gps_address);
            
            const response = await fetch('https://ghanapostgps.sperixlabs.org/get-location', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                },
                body: formData.toString()
            });
            const result = await response.json();
            
            if (result.found && result.data?.Table?.length > 0) {
                const data = result.data.Table[0];
                setNewAddress(prev => ({
                    ...prev,
                    street_address: data.Street && data.Street !== '[UNKNOWN]' ? data.Street : prev.street_address,
                    area: data.Area || prev.area,
                    city: data.District || prev.city,
                    state: data.Region || prev.state,
                    postal_code: data.PostCode || prev.postal_code,
                    latitude: data.CenterLatitude ? Number(data.CenterLatitude).toFixed(8) : prev.latitude,
                    longitude: data.CenterLongitude ? Number(data.CenterLongitude).toFixed(8) : prev.longitude,
                    country: 'Ghana'
                }));
                toast.success('Address details fetched successfully!');
            } else {
                toast.error('Could not find details for this GPS address.');
            }
        } catch (error) {
            console.error('GPS Fetch Error', error);
            toast.error('Error fetching GPS details.');
        } finally {
            setFetchingGps(false);
        }
    };

    const handleUseCurrentLocation = () => {
        if (!navigator.geolocation) {
            toast.error('Geolocation is not supported by your browser.');
            return;
        }

        setFetchingGps(true);
        navigator.geolocation.getCurrentPosition(
            async (position) => {
                try {
                    const lat = position.coords.latitude;
                    const long = position.coords.longitude;
                    
                    const formData = new FormData();
                    formData.append('lat', lat.toString());
                    formData.append('long', long.toString());
                    
                    const response = await fetch('https://ghanapostgps.sperixlabs.org/get-address', {
                        method: 'POST',
                        body: formData
                    });
                    const result = await response.json();
                    
                    if (result.found && result.data?.Table?.length > 0) {
                        const data = result.data.Table[0];
                        setNewAddress(prev => ({
                            ...prev,
                            gps_address: data.GPSName || prev.gps_address,
                            street_address: data.Street && data.Street !== '[UNKNOWN]' ? data.Street : prev.street_address,
                            area: data.Area || prev.area,
                            city: data.District || prev.city,
                            state: data.Region || prev.state,
                            postal_code: data.PostCode || prev.postal_code,
                            latitude: Number(lat).toFixed(8),
                            longitude: Number(long).toFixed(8),
                            country: 'Ghana'
                        }));
                        toast.success('Location fetched successfully!');
                    } else {
                        toast.error('Could not determine GhanaPostGPS address for your location.');
                    }
                } catch (error) {
                    console.error('Location API Error', error);
                    toast.error('Error resolving current location.');
                } finally {
                    setFetchingGps(false);
                }
            },
            (error) => {
                console.error(error);
                toast.error('Unable to retrieve your location. Please check browser permissions.');
                setFetchingGps(false);
            }
        );
    };

    useEffect(() => {
        fetchAddresses();
        fetchSavedCards();
        fetchCart();
    }, []);

    useEffect(() => {
        if (user && !newAddress.contact_name) {
            setNewAddress(prev => ({
                ...prev,
                contact_name: `${user.first_name} ${user.last_name}`,
                contact_phone: user.phone || ''
            }));
        }
    }, [user]);

    const fetchAddresses = async () => {
        try {
            const response = await AddressService.getAddresses();
            setAddresses(response.data.results || []);
            if (response.data.results?.length > 0) {
                const defaultAddr = response.data.results.find(a => a.is_default);
                setSelectedAddressId(defaultAddr ? defaultAddr.id : response.data.results[0].id);
            } else {
                setIsAddingNew(true);
            }
        } catch (error) {
            console.error('Failed to fetch addresses', error);
        }
    };

    const fetchSavedCards = async () => {
        try {
            const response = await UserService.getSavedCards();
            setSavedCards(response.data.results || response.data || []);
        } catch (error) {
            console.error('Failed to fetch saved cards', error);
        }
    };

    const handleAddressChange = (e) => {
        setNewAddress({ ...newAddress, [e.target.name]: e.target.value });
    };

    const handleSaveAddress = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const res = await AddressService.createAddress(newAddress);
            setAddresses([...addresses, res.data]);
            setSelectedAddressId(res.data.id);
            setIsAddingNew(false);
            toast.success('Address saved!');
        } catch (error) {
            console.error(error);
            toast.error('Failed to save address');
        } finally {
            setLoading(false);
        }
    };

    const handlePlaceOrder = async () => {
        if (!selectedAddressId) {
            toast.error('Please select a shipping address');
            return;
        }

        setLoading(true);
        try {
            const orderData = {
                shipping_address_id: selectedAddressId,
                billing_address_id: selectedAddressId,
                payment_method: 'paystack',
                coupon_code: couponCode
            };

            const response = await OrderService.createOrder(orderData);
            const order = response.data;

            const paymentPayload = {
                order_id: order.id,
                email: user.email,
                callback_url: `${window.location.origin}/payment/callback`,
                payment_method: paymentMethod === 'new_card' ? 'paystack' : `saved_card_${paymentMethod}`
            };

            if (paymentMethod === 'new_card' && saveCard) {
                localStorage.setItem('save_card', 'true');
            } else {
                localStorage.removeItem('save_card');
            }

            const paymentResponse = await PaymentService.initializePayment(paymentPayload);
            const paymentResult = paymentResponse.data;

            if (paymentResult.authorization_url) {
                window.location.href = paymentResult.authorization_url;
            } else if (paymentResult.status === 'success' || paymentResult.status === 'pending') {
                toast.success('Payment successful!');
                navigate('/orders');
            } else {
                toast.error('Payment initialization failed');
                navigate(`/orders/${order.id}`);
            }

        } catch (error) {
            console.error(error);
            toast.error(error.response?.data?.error || 'Failed to place order');
            setLoading(false);
        }
    };

    return (
        <div className="max-w-7xl mx-auto px-6 py-12 md:py-20">
            <h1 className="font-serif text-4xl mb-12">Checkout</h1>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-24">
                {/* Left Column: Address & Payment */}
                <div className="lg:col-span-2 space-y-12">

                    {/* Shipping Address */}
                    <div className="border-b border-gray-100 pb-12">
                        <div className="flex items-center justify-between mb-8">
                            <h2 className="font-serif text-2xl">Shipping Address</h2>
                            <button
                                onClick={() => setIsAddingNew(!isAddingNew)}
                                className="text-xs uppercase tracking-widest text-black hover:opacity-60 border-b border-black pb-0.5"
                            >
                                {isAddingNew ? 'Cancel' : '+ Add New'}
                            </button>
                        </div>

                        {!isAddingNew ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {addresses.map(addr => (
                                    <div
                                        key={addr.id}
                                        onClick={() => setSelectedAddressId(addr.id)}
                                        className={`p-6 bg-gray-50 cursor-pointer transition-all relative group
                                            ${selectedAddressId === addr.id ? 'ring-1 ring-black' : 'hover:bg-gray-100'}
                                        `}
                                    >
                                        <div className="flex items-center gap-2 mb-2 font-bold text-sm">
                                            <MapPin size={14} />
                                            {addr.contact_name}
                                        </div>
                                        <div className="text-sm font-light text-gray-600 space-y-1">
                                            <p>{addr.street_address}</p>
                                            <p>{addr.city}, {addr.state} {addr.postal_code}</p>
                                            <p>{addr.country}</p>
                                        </div>
                                        {selectedAddressId === addr.id && (
                                            <div className="absolute top-6 right-6">
                                                <div className="w-2 h-2 bg-black rounded-full" />
                                            </div>
                                        )}
                                    </div>
                                ))}
                                {addresses.length === 0 && (
                                    <div className="col-span-full py-12 text-center bg-gray-50 text-gray-400 text-sm uppercase tracking-widest">
                                        No addresses saved.
                                    </div>
                                )}
                            </div>
                        ) : (
                            <form onSubmit={handleSaveAddress} className="space-y-6 max-w-lg">
                                
                                <div className="space-y-3 bg-white border border-gray-200 p-4 rounded-lg mb-6">
                                    <div className="flex flex-col sm:flex-row gap-3 items-end">
                                        <div className="flex-1 w-full">
                                            <InputField name="gps_address" value={newAddress.gps_address || ''} onChange={handleAddressChange} label="GhanaPostGPS Address (Optional)" placeholder="e.g. AK-484-9321" />
                                        </div>
                                        <Button type="button" onClick={fetchDetailsFromGps} disabled={fetchingGps} variant="outline" className="w-full sm:w-auto shrink-0 mb-1 border-black text-black hover:bg-black hover:text-white">
                                            {fetchingGps ? 'Loading...' : 'Verify'}
                                        </Button>
                                    </div>
                                    <div className="relative py-2 flex items-center">
                                        <div className="flex-grow border-t border-gray-200"></div>
                                        <span className="flex-shrink-0 mx-4 text-gray-400 text-xs uppercase tracking-widest">or</span>
                                        <div className="flex-grow border-t border-gray-200"></div>
                                    </div>
                                    <Button type="button" onClick={handleUseCurrentLocation} disabled={fetchingGps} className="w-full justify-center text-sm bg-blue-50 text-blue-700 hover:bg-blue-100 border-none transition-colors">
                                        <MapPin size={16} className="mr-2" /> Use My Current Location
                                    </Button>
                                </div>

                                <div className="grid grid-cols-2 gap-6">
                                    <InputField name="street_address" value={newAddress.street_address} onChange={handleAddressChange} label="Street Name" required />
                                    <InputField name="area" value={newAddress.area} onChange={handleAddressChange} label="Area" required />
                                </div>
                                <div className="grid grid-cols-2 gap-6">
                                    <InputField name="contact_name" value={newAddress.contact_name} onChange={handleAddressChange} label="Full Name" required />
                                    <InputField name="contact_phone" value={newAddress.contact_phone} onChange={handleAddressChange} label="Phone" required />
                                </div>
                                <InputField name="apartment" value={newAddress.apartment} onChange={handleAddressChange} label="Apartment (Optional)" />
                                <div className="grid grid-cols-2 gap-6">
                                    <InputField name="city" value={newAddress.city} onChange={handleAddressChange} label="City" required />
                                    <InputField name="state" value={newAddress.state} onChange={handleAddressChange} label="State" required />
                                </div>
                                <div className="grid grid-cols-2 gap-6">
                                    <InputField name="postal_code" value={newAddress.postal_code} onChange={handleAddressChange} label="Zip Code" required />
                                    <InputField name="country" value={newAddress.country} onChange={handleAddressChange} label="Country" required />
                                </div>
                                <Button type="submit" disabled={loading}>
                                    {loading ? 'Saving...' : 'Save Address'}
                                </Button>
                            </form>
                        )}
                    </div>

                    {/* Payment Method */}
                    <div>
                        <h2 className="font-serif text-2xl mb-8">Payment Method</h2>

                        <div className="space-y-4">
                            {savedCards.map(card => (
                                <div
                                    key={card.id}
                                    onClick={() => setPaymentMethod(card.id)}
                                    className={`p-6 bg-gray-50 flex items-center justify-between cursor-pointer transition-all
                                        ${paymentMethod === card.id ? 'ring-1 ring-black' : 'hover:bg-gray-100'}
                                    `}
                                >
                                    <div className="flex items-center gap-4">
                                        <CreditCard size={20} className="text-gray-400" />
                                        <div>
                                            <div className="font-bold text-sm uppercase tracking-wider">{card.brand} •••• <span className="font-sans">{card.last4}</span></div>
                                            <div className="text-xs text-gray-500 mt-1">Expires <span className="font-sans">{card.exp_month}/{card.exp_year}</span></div>
                                        </div>
                                    </div>
                                    {paymentMethod === card.id && <div className="w-2 h-2 bg-black rounded-full" />}
                                </div>
                            ))}

                            <div
                                onClick={() => setPaymentMethod('new_card')}
                                className={`p-6 bg-gray-50 flex flex-col cursor-pointer transition-all
                                    ${paymentMethod === 'new_card' ? 'ring-1 ring-black' : 'hover:bg-gray-100'}
                                `}
                            >
                                <div className="flex items-center justify-between w-full">
                                    <div className="flex items-center gap-4">
                                        <Plus size={20} className="text-gray-400" />
                                        <span className="font-bold text-sm uppercase tracking-wider">Online Payment</span>
                                    </div>
                                    {paymentMethod === 'new_card' && <div className="w-2 h-2 bg-black rounded-full" />}
                                </div>

                                {paymentMethod === 'new_card' && (
                                    <div className="mt-4 pl-9">
                                        <label className="flex items-center gap-3 text-sm text-gray-600 cursor-pointer select-none group">
                                            <div className={`w-4 h-4 border border-gray-300 flex items-center justify-center transition-colors ${saveCard ? 'bg-black border-black text-white' : 'bg-white group-hover:border-black'}`}>
                                                {saveCard && <Check size={10} />}
                                            </div>
                                            <input
                                                type="checkbox"
                                                checked={saveCard}
                                                onChange={(e) => setSaveCard(e.target.checked)}
                                                className="hidden"
                                            />
                                            Save for future purchases
                                        </label>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Column: Order Summary */}
                <div className="h-fit">
                    <div className="bg-gray-50 p-8 sticky top-24">
                        <h2 className="font-serif text-2xl mb-8">Summary</h2>

                        <div className="space-y-4 mb-8">
                            {cart.items.map(item => (
                                <div key={item.id} className="flex gap-4">
                                    <div className="w-12 h-16 bg-white flex-shrink-0 border border-gray-100 overflow-hidden">
                                        {item.variant_details?.product_image && <img src={item.variant_details.product_image} alt="" className="w-full h-full object-cover" />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-medium text-sm truncate">{item.variant_details?.product_name}</p>
                                        <p className="text-xs text-gray-500 mt-1">Qty: <span className="font-sans">{item.quantity}</span></p>
                                    </div>
                                    <div className="font-bold text-sm font-sans">{formatPrice(item.price * item.quantity)}</div>
                                </div>
                            ))}
                        </div>

                        <div className="border-t border-gray-200 pt-6 space-y-3 text-sm">
                            <div className="flex justify-between text-gray-500">
                                <span>Subtotal</span>
                                <span className="font-sans">{formatPrice(cart.total_price)}</span>
                            </div>
                            <div className="flex justify-between text-gray-500">
                                <span>Shipping</span>
                                <span className="font-sans">{formatPrice(10.00)}</span>
                            </div>
                            <div className="flex justify-between text-gray-500">
                                <span>Tax (10%)</span>
                                <span className="font-sans">{formatPrice(parseFloat(cart.total_price) * 0.10)}</span>
                            </div>
                            {discount > 0 && (
                                <div className="flex justify-between text-green-600">
                                    <span>Discount</span>
                                    <span className="font-sans">-{formatPrice(discount)}</span>
                                </div>
                            )}
                            <div className="flex justify-between font-serif text-xl pt-4 border-t border-gray-200 mt-4">
                                <span>Total</span>
                                <span className="font-sans font-medium">{formatPrice((parseFloat(cart.total_price) * 1.10 + 10 - discount))}</span>
                            </div>
                        </div>

                        <Button
                            onClick={handlePlaceOrder}
                            disabled={loading || !selectedAddressId}
                            className="w-full mt-8 justify-center"
                        >
                            {loading ? 'Processing...' : `Place Order`}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CheckoutPage;
